import type { CSSProperties } from 'react'
import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react'
import streamIcon from '@/assets/stream-icon.jpg'
import { useTransitionStore } from '@/shared/stores'
import { useOperatorStore } from '@/shared/stores'
import { useSettingsStore } from '@/shared/stores/settings-store'

interface Props {
  onComplete: () => void
}

/**
 * SplashScreen — branded reveal layer that sits over the unified canvas.
 *
 * The canvas itself (StreamParticleCanvas) is rendered at the App root and
 * shows the flowing river. This component is just the centred logo / title /
 * hint stack with a transparent background — clicking anywhere on the
 * fullscreen container triggers the river → particles morph in the canvas
 * and fades out the splash content.
 *
 * Timing:
 *   - Click → triggerMorph() flips canvas phase to 'morphing' immediately.
 *   - Splash content fades over SPLASH_FADE_MS so the river is partly
 *     fragmented when the logo finishes fading.
 *   - After SPLASH_FADE_MS, onComplete() fires and App.tsx flips to
 *     OperatorSelector. The canvas is still in 'morphing' phase at that
 *     point, so the operator UI's entrance fade overlaps with the particles
 *     snapping into the orbital ring — one continuous transition rather
 *     than splash → blank canvas → selector pop-in.
 */

const SPLASH_FADE_MS = 2100

export function SplashScreen({ onComplete }: Props) {
  const [fadeOut, setFadeOut] = useState(false)
  const [showLogo, setShowLogo] = useState(false)
  const dismissedRef = useRef(false)

  // Mark the splash as active so the canvas's 'river' render branch will
  // actually run. The canvas refuses to render the river when this flag is
  // false — that's the structural guarantee that the river never bleeds
  // into the main app, regardless of what state `phase` ends up in.
  //
  // useLayoutEffect runs synchronously after render, before paint, so the
  // canvas's first requestAnimationFrame tick after splash mount sees the
  // flag as true.
  useLayoutEffect(() => {
    useTransitionStore.getState().setSplashActive(true)
    return () => {
      useTransitionStore.getState().setSplashActive(false)
    }
  }, [])

  const handleDismiss = useCallback(() => {
    if (dismissedRef.current) return
    dismissedRef.current = true

    // Branch on whether this is a "returning user" (already-selected
    // operator persisted from a previous session) AND has chosen a specific
    // landing page. Three scenarios:
    //
    //   - First-time user / operator change: river → morphing → particles.
    //     The OperatorSelector renders over the orbital particles. The
    //     user picks an operator there.
    //
    //   - Returning user, kodeon-splash mode (default): river → morphing →
    //     particles. The AppShell mounts visible (sidebar real and
    //     interactive) with WelcomePage occupying the empty content area.
    //     The particles, already in 'particles' phase, spell KODEON inside
    //     the WelcomePage's bounding rect (driven by setParticleText).
    //
    //   - Returning user, specific-page mode: river → morphing-to-shell →
    //     trace-hold → ui-revealing → particles-fading → shell-revealed.
    //     The dots trace the shape of the destination page (sidebar,
    //     header, content) before the page fades in behind them.
    const operator = useOperatorStore.getState().selected
    const isReturning = !!(operator && operator.consumer_id)
    const landingPage = useSettingsStore.getState().landingPage

    if (isReturning && landingPage.kind === 'route') {
      useTransitionStore.getState().triggerShellMorph(landingPage.route)
    } else {
      // Both first-time and returning-with-kodeon-splash use the standard
      // morph. For the kodeon-splash case, the AppShell's WelcomePage
      // takes over from there with its own particleText push.
      useTransitionStore.getState().triggerMorph()
    }

    setFadeOut(true)
    // Unmount the splash as soon as its content has fully faded — NOT when
    // the morph completes. Doing this lets the OperatorSelector mount
    // ~SPLASH_FADE_MS into the morph and run its own entrance fade in
    // parallel with the particles snapping into formation, instead of
    // popping in 500ms after a blank canvas. The morph keeps running
    // (driven by the transition store) regardless of when the splash
    // unmounts.
    setTimeout(onComplete, SPLASH_FADE_MS)
  }, [onComplete])

  useEffect(() => {
    const handleClick = () => handleDismiss()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.repeat) handleDismiss()
    }

    window.addEventListener('click', handleClick)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('click', handleClick)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleDismiss])

  useEffect(() => {
    const logoTimer = setTimeout(() => setShowLogo(true), 500)
    return () => clearTimeout(logoTimer)
  }, [])

  const containerStyle: CSSProperties = {
    ...styles.container,
    opacity: fadeOut ? 0 : 1,
    transition: fadeOut
      ? `opacity ${SPLASH_FADE_MS}ms ease-out`
      : 'opacity 600ms ease-out',
  }

  const logoContainerStyle: CSSProperties = {
    ...styles.logoContainer,
    opacity: showLogo ? 1 : 0,
    transform: showLogo
      ? 'translateY(0) scale(1)'
      : 'translateY(20px) scale(0.95)',
    transition: 'all 800ms cubic-bezier(0.16, 1, 0.3, 1)',
  }

  return (
    <div style={containerStyle}>
      <div style={logoContainerStyle}>
        <div style={styles.logoIcon}>
          <img src={streamIcon} alt="Kodeon Stream" style={styles.logoImage} />
        </div>

        <h1 style={styles.title}>Kodeon Stream</h1>
        <p style={styles.subtitle}>Real-time Platform Intelligence</p>
        <p style={styles.hint} className="pulse">
          Click or press any key to continue
        </p>
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    // Transparent so the unified canvas (mounted at the App root) shows
    // through. The body element provides bg-secondary via global.css for the
    // brief frame before the canvas first paints.
    background: 'transparent',
  },
  logoContainer: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  logoIcon: {
    display: 'flex',
    width: '100px',
    height: '100px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '24px',
    border: '1px solid var(--accent-bg-visible)',
    background: 'var(--accent-bg-light)',
    boxShadow: '0 0 40px var(--accent-bg-medium)',
    overflow: 'hidden',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  title: {
    margin: 0,
    fontSize: '48px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    color: 'var(--text-muted)',
  },
  hint: {
    margin: '32px 0 0 0',
    fontSize: '13px',
    fontWeight: 400,
    letterSpacing: '0.02em',
    color: 'var(--text-faint)',
    /* Animation applied via className="pulse" - see global.css */
  },
}
